'use client';

import { useEffect, useMemo } from 'react';

import { Controller, motion, type Control } from '@/libs';
import { useDrawerStore } from '@/stores';
import type { RegistrationFormData } from '@/types/models/CustomerRegistrationForm';
import { normalizeSocialNameInput } from '@/utils';

import {
  useUserRegisterForm,
  type RegisteredUserData,
  type RegistrationSeed,
} from '../hooks/useUserRegisterForm';

import {
  Button,
  Checkbox,
  formatPhone,
  FormField,
  Icon,
  InputForm,
  SocialNameInfoTooltip,
} from '../../ui';

type UserRegisterFormProps = {
  seed: RegistrationSeed;
  onRegisterSuccess: (data: RegisteredUserData) => void;
  onBack: () => void;
};

export const UserRegisterForm = ({
  seed,
  onRegisterSuccess,
  onBack,
}: UserRegisterFormProps) => {
  const {
    control,
    errors,
    isLoading,
    isEmailRequired,
    isDirty,
    showSocialName,
    setShowSocialName,
    checkboxes,
    handleSubmit,
  } = useUserRegisterForm({ seed, onRegisterSuccess });

  const updateShouldCallPreventClose = useDrawerStore(
    (state) => state.updateShouldCallPreventClose,
  );

  useEffect(() => {
    updateShouldCallPreventClose(isDirty);
    return () => updateShouldCallPreventClose(false);
  }, [isDirty, updateShouldCallPreventClose]);

  const termsOfServiceLabel = useMemo(() => {
    const { prefixText, links, brandText, required } = checkboxes.termsOfService;

    const linkElements = links.map((link, index) => (
      <span key={`${link.href}-${index}`}>
        {index > 0 ? (index === links.length - 1 ? ' e ' : ', ') : ' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
          href={link.href}
        >
          {link.text}
        </a>
      </span>
    ));

    return (
      <span>
        {prefixText}
        {linkElements}
        {brandText ? ` ${brandText}` : ''}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
    );
  }, [checkboxes.termsOfService]);

  return (
    <motion.div
      data-cy="user-register-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-4"
    >
      <p className="text-sm text-foreground text-center">
        Confirme seus dados para criar sua conta.
      </p>

      <form
        data-cy="user-register-form-form"
        onSubmit={handleSubmit}
        className="flex flex-col mx-auto w-full md:max-w-[336px]"
      >
        <div
          data-cy="user-register-form-fields"
          className="flex flex-col gap-y-4 max-h-[55vh] overflow-y-auto overscroll-contain px-1.5 -mx-1.5 pb-2"
        >
          <FormField error={errors.document} label="CPF">
            <InputForm
              data-cy="user-register-form-document-input"
              control={control}
              name="document"
              inputMode="numeric"
              autoComplete="off"
              disabled
            />
          </FormField>

          <FormField error={errors.fullname} label="Nome completo">
            <InputForm
              data-cy="user-register-form-fullname-input"
              control={control}
              name="fullname"
              autoComplete="name"
              disabled
            />
          </FormField>

          <Controller
            control={control as Control<RegistrationFormData>}
            name="hasSocialName"
            render={({ field, fieldState }) => (
              <FormField error={fieldState.error}>
                <Checkbox
                  className="text-xs"
                  checked={Boolean(field.value)}
                  labelClassName="text-xs"
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    setShowSocialName(Boolean(checked));
                  }}
                  label="Informar Nome Social"
                  data-cy="user-register-form-has-social-name-checkbox"
                />
                <SocialNameInfoTooltip />
              </FormField>
            )}
          />

          {showSocialName && (
            <FormField error={errors.socialName} label="Nome social">
              <InputForm
                data-cy="user-register-form-socialname-input"
                control={control}
                name="socialName"
                placeholder="Nome social"
                inputMode="text"
                autoComplete="name"
                formatFunction={normalizeSocialNameInput}
              />
            </FormField>
          )}

          <FormField error={errors.email} label="E-mail" required={isEmailRequired}>
            <InputForm
              data-cy="user-register-form-email-input"
              control={control}
              name="email"
              required={isEmailRequired}
              aria-required={isEmailRequired}
              placeholder={isEmailRequired ? 'Email (obrigatório)' : 'Email (opcional)'}
              inputMode="email"
              autoComplete="email"
              formatFunction={(text: string) => {
                const cleaned = text.replace(/\s/g, '');
                return cleaned === '' ? undefined : cleaned;
              }}
            />
          </FormField>

          <FormField error={errors.phone} label="Celular" required>
            <InputForm
              data-cy="user-register-form-phone-input"
              control={control}
              name="phone"
              rules={{ deps: ['phoneConfirmation'] }}
              placeholder="(00) 00000-0000"
              formatFunction={formatPhone}
              inputMode="tel"
              autoComplete="tel"
            />
          </FormField>

          <FormField error={errors.phoneConfirmation} label="Confirmar celular" required>
            <InputForm
              data-cy="user-register-form-phone-confirmation-input"
              control={control}
              name="phoneConfirmation"
              rules={{ deps: ['phone'] }}
              placeholder="(00) 00000-0000"
              formatFunction={formatPhone}
              inputMode="tel"
              autoComplete="tel"
            />
          </FormField>

          {checkboxes.marketingOptIn.visible && (
            <Controller
              control={control as Control<RegistrationFormData>}
              name="MarketingOptIn"
              render={({ field, fieldState }) => (
                <FormField error={fieldState.error}>
                  <Checkbox
                    data-cy="user-register-form-marketing-checkbox"
                    className="text-xs"
                    checked={Boolean(field.value)}
                    labelClassName="text-xs"
                    onCheckedChange={field.onChange}
                    label={
                      <span>
                        {checkboxes.marketingOptIn.label}
                        {checkboxes.marketingOptIn.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </span>
                    }
                  />
                </FormField>
              )}
            />
          )}

          {checkboxes.termsOfService.visible && (
            <Controller
              control={control as Control<RegistrationFormData>}
              name="isTermsOfServiceAccepted"
              render={({ field, fieldState }) => (
                <FormField error={fieldState.error}>
                  <Checkbox
                    data-cy="user-register-form-terms-checkbox"
                    id="isTermsOfServiceAccepted"
                    checked={Boolean(field.value)}
                    labelClassName="text-xs"
                    onCheckedChange={field.onChange}
                    label={termsOfServiceLabel}
                  />
                </FormField>
              )}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 pt-4">
          <Button
            data-cy="user-register-form-submit-button"
            type="submit"
            loading={isLoading}
            disabled={isLoading}
            loadingText="Criando..."
            variant="primary"
            size="large"
            className="h-12 w-full flex items-center text-background text-base uppercase"
          >
            <span className="mx-auto">Criar Conta</span>
            <Icon.ChevronRight width={24} height={24} />
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="mx-auto"
            disabled={isLoading}
            onClick={onBack}
          >
            Voltar
          </Button>
        </div>
      </form>
    </motion.div>
  );
};
